/**
 * Percent normalization utilities
 * 
 * GLOBAL RULE: All percentages are stored internally as DECIMAL format.
 * - 21% → 0.21
 * - 27% → 0.27
 * 
 * Never store 21 or 27 for percent fields.
 * Never store strings like "21%".
 */

/**
 * Normalize a percent input to decimal format
 * @param {unknown} input - The input value (string like "21%", number like 21 or 0.21)
 * @returns {number|null} - Normalized decimal value (0.21) or null if invalid
 * 
 * Examples:
 *   normalizePercent("21%") → 0.21
 *   normalizePercent("27") → 0.27
 *   normalizePercent(27) → 0.27
 *   normalizePercent(0.27) → 0.27
 *   normalizePercent(0.0027) → 0.0027
 *   normalizePercent(null) → null
 *   normalizePercent("invalid") → null
 */
export function normalizePercent(input) {
  // Handle null/undefined
  if (input === null || input === undefined) {
    return null;
  }

  let parsedValue;

  // Handle string input
  if (typeof input === 'string') {
    // Strip % symbol and trim whitespace
    const cleaned = input.replace('%', '').trim();
    parsedValue = parseFloat(cleaned);
  } else if (typeof input === 'number') {
    parsedValue = input;
  } else {
    return null;
  }

  // Check for NaN
  if (Number.isNaN(parsedValue)) {
    return null;
  }

  // If value > 1, assume whole percent form (e.g., 21) and convert to decimal
  if (parsedValue > 1) {
    return parsedValue / 100;
  }

  // Value <= 1, assume already decimal (e.g., 0.21)
  return parsedValue;
}

/**
 * Format a decimal percent for UI display
 * @param {number|null} decimal - The decimal value (e.g., 0.21)
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} - Formatted percent string (e.g., "21.0%")
 * 
 * Examples:
 *   formatPercent(0.21) → "21.0%"
 *   formatPercent(0.275) → "27.5%"
 *   formatPercent(null) → "Not available"
 *   formatPercent(0.21, 0) → "21%"
 */
export function formatPercent(decimal, decimals = 1) {
  if (decimal === null || decimal === undefined) {
    return 'Not available';
  }

  // Defensive guardrail: if decimal > 1, log warning and auto-normalize once
  if (decimal > 1) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[formatPercent] Received value > 1 (${decimal}), auto-normalizing by dividing by 100`);
    }
    decimal = decimal / 100;
  }

  return `${(decimal * 100).toFixed(decimals)}%`;
}

/**
 * Normalize all percent fields in an object recursively
 * @param {Object} obj - The object to normalize
 * @param {string[]} fields - Array of field names to treat as percents
 * @returns {Object} - New object with normalized percent values
 */
export function normalizePercentFields(obj, fields) {
  if (!obj || typeof obj !== 'object') {
    return obj;
  }

  const result = { ...obj };

  for (const key of Object.keys(result)) {
    if (fields.includes(key)) {
      result[key] = normalizePercent(result[key]);
    } else if (typeof result[key] === 'object' && result[key] !== null) {
      // Recursively normalize nested objects
      result[key] = normalizePercentFields(result[key], fields);
    }
  }

  return result;
}

/**
 * Common percent field names used across the application
 */
export const PERCENT_FIELDS = [
  'top1Percent',
  'top3Percent', 
  'top5Percent',
  'customerConcentration',
  'savingsRate',
  'savingsRateAssumption',
  'attachRate',
  'benefitsLoad',
  'confidence',
  'probability',
  'captureProbabilityPct',
  'taxonomyConfidence',
  'impactPct',
  'adjustmentPct',
  'placeholderPct',
  'overtimePct',
  'grossMargin',
  'confidencePct'
];

/**
 * Normalize customer concentration data
 * @param {Object} concentration - Raw customer concentration data
 * @returns {Object} - Normalized concentration with decimal percents
 */
export function normalizeCustomerConcentration(concentration) {
  if (!concentration || typeof concentration !== 'object') {
    return {
      period: null,
      top1Percent: null,
      top3Percent: null,
      top5Percent: null
    };
  }

  return {
    period: concentration.period || null,
    top1Percent: normalizePercent(concentration.top1Percent),
    top3Percent: normalizePercent(concentration.top3Percent),
    top5Percent: normalizePercent(concentration.top5Percent)
  };
}

export default {
  normalizePercent,
  formatPercent,
  normalizePercentFields,
  normalizeCustomerConcentration,
  PERCENT_FIELDS
};
